// Keluaran dari alur PBJ, on the Template page for a Non-Penyedia: the
// Output/Dokumen of the PBJ cycle's activities (GET /templates/outputs),
// each with Buat dokumen, which drafts it through the chat, and Unduh .docx,
// the Word skeleton at once (./docx.ts).
//   toolbar   search, Tahap, and an optional PBJ project: picking one narrows
//             the list to the project's route and drafts inside it, with
//             its data (route, method, instansi, pagu, stage)
//   groups    rows per stage: the output, the activity and its pelaku, the
//             diagram; a decision node carries "Titik keputusan"
import { useState } from "react";
import * as API from "../api";
import { formatCount } from "../lib/format";
import { useFeatures } from "../shell/features";
import { Badge, EmptyState, Notice, SearchInput, SectionHead, SkeletonRows, useAsync } from "../ui/components";
import { Glyph } from "../ui/icons";
import { canDocx, useDocx } from "./docx";
import { canDraft, useDraft } from "./drafts";

function fold(text: string): string {
  return text.toLowerCase().normalize("NFKD").replace(/\p{M}/gu, "");
}

export function filterOutputs(list: API.PbjOutput[], q: string, stage: string): API.PbjOutput[] {
  const terms = fold(q).split(/\s+/).filter(Boolean);
  return list.filter((o) => {
    if (stage && o.stage !== stage) return false;
    const hay = fold([o.output, o.activity, o.actor, o.diagramTitle].join(" "));
    return terms.every((term) => hay.includes(term));
  });
}

export function PbjOutputs() {
  const features = useFeatures();
  const projectsOn = features.has("projects");
  const drafting = canDraft(features);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [projectId, setProjectId] = useState("");
  const projects = useAsync(() => (projectsOn ? API.listPbjProjects() : Promise.resolve([] as API.PbjProject[])), [projectsOn]);
  const list = useAsync(() => API.getPbjOutputs(projectId || undefined), [projectId]);
  const draft = useDraft();
  const docxOn = canDocx(features);
  const docx = useDocx();

  const data = list.data;
  const all = data?.outputs ?? [];
  const shown = filterOutputs(all, q, stage);
  const groups = (data?.stages ?? []).map((s) => ({ stage: s, items: shown.filter((o) => o.stage === s.id) })).filter((g) => g.items.length > 0);
  const lead = data?.project
    ? `${formatCount(all.length)} keluaran pada jalur project ${data.project.name}. Draf disusun di project itu dengan data paketnya.`
    : `${formatCount(all.length)} keluaran kegiatan dari alur PBJ. Pilih project PBJ agar draf memakai data paketnya.`;

  return (
    <section className="region pbj-outputs" aria-labelledby="tp-outputs-title">
      <SectionHead id="tp-outputs-title" title="Keluaran dari alur PBJ" lead={data ? lead : "Memuat keluaran"} />
      <div className="toolbar">
        <div className="field-row" data-cols="3">
          <div className="field">
            <label htmlFor="tp-out-q">Cari keluaran</label>
            <SearchInput id="tp-out-q" value={q} onChange={setQ} placeholder="Dokumen, kegiatan, atau pelaku" />
          </div>
          <div className="field">
            <label htmlFor="tp-out-stage">Tahap</label>
            <select id="tp-out-stage" value={stage} onChange={(e) => setStage(e.currentTarget.value)}>
              <option value="">Semua tahap</option>
              {(data?.stages ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          {projectsOn ? (
            <div className="field">
              <label htmlFor="tp-out-project">Project PBJ</label>
              <select id="tp-out-project" value={projectId} onChange={(e) => setProjectId(e.currentTarget.value)}>
                <option value="">Tanpa project</option>
                {(projects.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </div>
      </div>

      {list.loading && !data ? (
        <SkeletonRows count={4} label="Memuat keluaran" />
      ) : list.error ? (
        <Notice tone="danger" title="Keluaran belum dapat dimuat" action={<button type="button" className="btn btn-secondary" onClick={list.reload}>Muat ulang</button>}>
          <p>{API.errorMessage(list.error)}</p>
        </Notice>
      ) : groups.length === 0 ? (
        <EmptyState icon="filter" title="Tidak ada keluaran yang cocok" body="Ubah kata kunci atau pilih semua tahap." />
      ) : (
        <div className="kb-groups">
          {groups.map((g) => (
            <div key={g.stage.id} className="pbj-docs-head">
              <h3 className="pbj-subtitle">
                {g.stage.label} ({formatCount(g.items.length)})
              </h3>
              <ul className="rows">
                {g.items.map((o) => {
                  const key = `${o.stage}:${o.nodeId}`;
                  return (
                    <li key={key} className="row">
                      <span className="row-main">
                        <span className="row-title">{o.output}</span>
                        <span className="row-meta">
                          {o.activity}. {o.actor}
                        </span>
                        <span className="row-meta">{o.diagramTitle}</span>
                        {o.decision ? (
                          <span>
                            <Badge tone="warning">Titik keputusan</Badge>
                          </span>
                        ) : null}
                        {draft.error?.key === key || docx.error?.key === key ? (
                          <span className="pk-item-error" role="alert">
                            <Glyph name="danger" size={16} />
                            <span>{draft.error?.key === key ? draft.error.message : docx.error?.message}</span>
                          </span>
                        ) : null}
                      </span>
                      {drafting || docxOn ? (
                        <span className="row-side">
                          {drafting ? (
                            <button
                              type="button"
                              className="btn btn-secondary"
                              disabled={draft.busy !== null}
                              aria-busy={draft.busy === key || undefined}
                              aria-label={`Buat dokumen ${o.output}`}
                              onClick={() => void draft.start(key, () => API.draftNodeOutput(o.nodeId, projectId || undefined), projectId || undefined)}
                            >
                              <Glyph name="docAdd" />
                              <span>{draft.busy === key ? "Pluto menyusun draf" : "Buat dokumen"}</span>
                            </button>
                          ) : null}
                          {docxOn ? (
                            <button
                              type="button"
                              className="btn btn-secondary"
                              disabled={docx.busy !== null}
                              aria-busy={docx.busy === key || undefined}
                              aria-label={`Unduh .docx ${o.output}`}
                              onClick={() => void docx.start(key, () => API.downloadNodeDocx(o.nodeId, projectId || undefined))}
                            >
                              <Glyph name="documentDownload" />
                              <span>{docx.busy === key ? "Menyiapkan" : "Unduh .docx"}</span>
                            </button>
                          ) : null}
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
